import { useEffect } from 'react';
import {
  fetchVideoProgress,
  postVideoProgress,
} from '@/core/required-videos-api';
import {
  emptyLocalProgress,
  loadLocalProgress,
  saveLocalProgress,
} from '@/core/required-videos-storage';
import type { VideoProgress } from '@/types/required-video';

export type Mode = 'server' | 'local';

// 10s persist cadence per the time-counter spec — implemented as a throttle
// (max one save every SAVE_INTERVAL_MS during continuous playback), NOT a
// debounce. A debounce here would never fire while the user is watching
// because every poll resets the timer, and the user's progress would only
// reach the server when they pause for >10s or unload the page.
export const SAVE_INTERVAL_MS = 10_000;

export type Refs = {
  timer: { current: number };
  dirty: { current: VideoProgress | null };
};

export async function loadInitial(
  videoKey: string,
  mode: Mode
): Promise<VideoProgress> {
  if (mode === 'local') return loadLocalProgress(videoKey);
  try {
    return await fetchVideoProgress(videoKey);
  } catch {
    return emptyLocalProgress(videoKey);
  }
}

export function persist(progress: VideoProgress, mode: Mode): void {
  if (mode === 'local') {
    saveLocalProgress(progress);
    return;
  }
  void postVideoProgress({
    video_key: progress.video_key,
    duration_secs: progress.duration_secs,
    seconds_watched: progress.seconds_watched,
    last_position: progress.last_position,
  }).catch(() => {
    /* server save failed — UI already updated; retry next tick */
  });
}

export function useInitialFetch(
  videoKey: string,
  mode: Mode,
  setLocal: (p: VideoProgress) => void
): void {
  // Deps are strict primitives — including the args object would re-run on
  // every render (parent recreates it) and clobber duration_secs.
  useEffect(() => {
    let cancelled = false;
    void loadInitial(videoKey, mode).then((p) => {
      if (!cancelled) setLocal(p);
    });
    return (): void => {
      cancelled = true;
    };
  }, [videoKey, mode, setLocal]);
}

export function usePersistOnHide(flushRef: { current: () => void }): void {
  useEffect(() => {
    const onHide = (): void => flushRef.current();
    window.addEventListener('pagehide', onHide);
    return (): void => window.removeEventListener('pagehide', onHide);
  }, [flushRef]);
}

export function makeFlush(refs: Refs, mode: Mode): () => void {
  return (): void => {
    if (refs.timer.current) {
      window.clearTimeout(refs.timer.current);
      refs.timer.current = 0;
    }
    if (refs.dirty.current) persist(refs.dirty.current, mode);
    refs.dirty.current = null;
  };
}

export function makeSetter(
  refs: Refs,
  setLocal: (p: VideoProgress) => void,
  flushRef: { current: () => void }
): (p: VideoProgress) => void {
  return (p) => {
    setLocal(p);
    refs.dirty.current = p;
    if (!refs.timer.current) {
      refs.timer.current = window.setTimeout(
        () => flushRef.current(),
        SAVE_INTERVAL_MS
      );
    }
  };
}
